import { z } from "zod";
import { common } from "../common";
import { stepGetSchema } from "../step/stepGetSchema";
import { sparseSegmentSchema } from "../shared/sparseSegmentSchema";
import { channelRateLimitsGetSchema } from "../shared/channelRateLimitsGetSchema";
import { conversionGetSchema } from "../conversion/conversionGetSchema";
import { orBehaviorTriggerGetSchema } from "../trigger/orBehaviorTriggerGetSchema";
import { filtersGetSchema, exclusionFiltersGetSchema } from "../filter/filtersGetSchema";
import { canvasVariationGetSchema } from "./canvasVariationGetSchema";

export const canvasGetSchema = z.object({
  api_workflow_id: common.uuid,
  app_group_id: common.objectId,
  automatic_distribution_optimization: z.boolean().nullable(), // null: 3_891 (0.78%)
  can_match_again_after_seconds: z.int().min(0).nullable(), // null: 37 (0.01%)
  channel_rate_limits: channelRateLimitsGetSchema,
  classic_migrated_workflow_id: common.objectId.nullable(),
  context_property_names_info: z
    .object({
      names: z.array(z.string().nullable()).nullable(), // null: 16 (0.00%)
    })
    .nullable(),
  conversion_behaviors: z.array(conversionGetSchema),
  conversion_errors: z.array(z.string()).nullable(),
  conversion_filter_descriptions: z.array(z.string()),
  created: z.object({
    c: common.objectId.nullable().optional(),
    d_id: z.string().optional(), // Only an object id 90.69% of the time.
    fm: z.literal(true).nullable().optional(),
    id: common.objectId.optional(),
    name: z.string(),
    time: common.unixTimestamp,
  }),
  currently_sending: z.boolean(),
  distribution_optimization_complete: z.boolean().nullable(),
  intelligent_selection_fixed_holdout_rollout: z.boolean().nullable().optional(),
  intelligent_selection_stopping_mode: z.string().nullable().optional(),
  draft_is_set_to_send_immediately: z.boolean(),
  enabled: z.boolean(),
  exclude_from_global_frequency_count: z.boolean(),
  exclusion_filters: exclusionFiltersGetSchema,
  exit_criteria: z
    .object({
      exclusion_filters: exclusionFiltersGetSchema,
      exit_event: orBehaviorTriggerGetSchema.nullable(),
      filters: filtersGetSchema,
      segment_ids: z.array(sparseSegmentSchema),
      using_v2_filters: z.boolean().nullable().optional(),
    })
    .nullable(),
  filters: filtersGetSchema,
  future_retargeting_data_offload_at: common.unixTimestamp,
  has_advanced_to_grid_view: z.boolean().nullable(),
  has_never_been_saved: z.boolean(),
  has_post_launch_draft: z.boolean(),
  id: common.objectId,
  ignore_global_frequency_capping: z.boolean(),
  is_archived: z.boolean(),
  is_convertible: z.boolean(),
  is_currently_expired: z.boolean(),
  is_draft: z.boolean(),
  is_idle: z.boolean(),
  is_read_only: z.boolean(),
  is_restoration_pending: z.boolean(),
  last_deleted_summaries_at: common.unixTimestamp,
  last_edit: z.object({
    c: common.objectId.nullable().optional(),
    d_id: z.string().optional(), // Only an object id 59% of the time.
    fm: z.literal(true).nullable().optional(),
    id: common.objectId.optional(),
    name: z.string(),
    time: common.unixTimestamp,
  }),
  last_offloaded_retargeting_data_at: common.unixTimestamp,
  last_restored_retargeting_data_at: common.unixTimestamp,
  last_sent_message: common.unixTimestamp,
  last_stopped_at: common.unixTimestamp,
  match_again_is_max_duration: z.boolean().nullable(),
  match_more_than_once: z.boolean(),
  max_recipients: z.int().min(1).nullable(),
  max_recipients_is_global: z.boolean(),
  total_number_of_potential_entries: z.int().min(0),
  maximum_duration: z.int().min(0).nullable(),
  priority_bucket: common.numericEnum([1, 2, 3]).nullable(),
  quiet_hours_source: z.enum(["none", "workspace", "custom"]).nullable().optional(),
  quiet_time_data: z.object({
    has_quiet_time: z.boolean(),
    quiet_end_hour: z.int().min(0).max(23).nullable(),
    quiet_end_minute: z.int().min(0).max(59).nullable(),
    quiet_start_hour: z.int().min(0).max(23).nullable(),
    quiet_start_minute: z.int().min(0).max(59).nullable(),
    send_after_quiet_time: z.boolean(),
  }),
  ready_to_offload_retargeting_data: z.boolean().nullable(),
  recipient_subscription_state: common.numericEnum([0, 1, 2]),
  schedule_data: z
    .object({
      content_card_delivery_control: z.null(),
      deliver_in_local_time: z.boolean(),
      display_text: z.string(),
      end_date: common.unixTimestamp.optional(),
      end_occurrences_count: z.int().min(1).optional(),
      frequency: z.enum(["Daily", "Weekly", "Monthly"]).optional(),
      has_quiet_time: z.boolean(),
      last_occurrence: z.int().optional(), // sometimes negative
      next_occurrence: common.unixTimestamp.nullable(),
      optimal_time_default_hour: z.int().nullable(),
      optimal_time_default_minute: z.int().nullable(),
      optimal_time_notification: z.boolean(),
      quiet_end_hour: z.int().min(0).max(23).nullable(),
      quiet_end_minute: z.int().min(0).max(59).nullable(),
      quiet_start_hour: z.int().min(0).max(23).nullable(),
      quiet_start_minute: z.int().min(0).max(59).nullable(),
      recurring: z.boolean(),
      repeat_interval: z.int().min(1).max(30).optional(),
      start_date_time: z.int(), // sometimes negative
      use_app_group_best_time: z.boolean().nullable(),
      weekdays: z
        .partialRecord(
          z.enum(["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]),
          z.literal(true).optional()
        )
        .optional(),
    })
    .nullable(),
  schedule_type: z.enum(["time_based", "action_based", "api_triggered"]),
  seed_exclude_already_sent_not_updated: z.boolean(),
  segments_data_array: z.array(sparseSegmentSchema),
  should_show_read_only_filter_sentence: z.literal(false),
  start_offloading_retargeting_data_at: common.unixTimestamp,
  step_ids: z.array(common.objectId),
  steps: z.array(stepGetSchema).nullable(),
  tag_names: z.array(z.string()),
  targets_android: z.boolean(),
  territory_ids: z.array(common.objectId),
  time_rate_limit: z.int().min(1).nullable(),
  total_sent: z.int().min(0),
  trigger_schedule_data: z
    .object({
      check_eligibility_at_impression_time: z.literal(false),
      delay_option: z.literal("immediately").nullable(),
      deliver_in_days: z.int().min(0).nullable(),
      deliver_in_local_time: z.boolean(),
      delivery_day: z
        .enum(["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"])
        .nullable(),
      delivery_time_without_zone: z
        .object({
          hour: z.int().min(0).max(23),
          min: z.int().min(0).max(59),
          sec: z.int().min(0).max(59),
        })
        .nullable(),
      duration_in_seconds: z.int().min(0).nullable(),
      end_time: common.unixTimestamp.nullable(),
      evaluate_segment_at_enqueue_time: z.literal(true),
      exception_event: z.null(),
      optimal_time_default_data: z.object({
        optimal_time_default_hour: z.int().min(0).max(23).nullable(),
        optimal_time_default_minute: z.int().min(0).max(59).nullable(),
        use_app_group_best_time: z.boolean().nullable(),
      }),
      quiet_time_data: z.object({
        has_quiet_time: z.boolean(),
        quiet_end_hour: z.int().min(0).max(23).nullable(),
        quiet_end_minute: z.int().min(0).max(59).nullable(),
        quiet_start_hour: z.int().min(0).max(23).nullable(),
        quiet_start_minute: z.int().min(0).max(59).nullable(),
      }),
      reevaluate_segment_at_send_time: z.literal(false),
      send_after_quiet_time: z.boolean().nullable(),
      start_time: common.unixTimestamp,
      trigger_delay_in_seconds: z.int().min(0).nullable(),
      trigger_event: orBehaviorTriggerGetSchema.nullable(),
    })
    .partial()
    .nullable(),
  using_v2_filters: z.boolean().nullable().optional(),
  variations: z.array(canvasVariationGetSchema),
  version: z.enum(["flow", "classic"]),
  volume_limit_daily: z.int().min(0).nullable(),
  workflow_description: z.string().nullable(),
  workflow_name: z.string(),
});
